import React, { useEffect, useState } from "react";
import { Smartphone, Upload, Plus, Trash2, Copy, CheckCircle2, AlertTriangle, ExternalLink, FileJson, ShoppingBag, Truck, Shield, Info } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

/**
 * Tenant Admin → Android App (TWA) — manage Customer, Rider, and Admin
 * Digital Asset Links separately. Each persona has its own package_name
 * and SHA-256 signing cert. All three merge into the single
 * /.well-known/assetlinks.json file.
 */
const PERSONAS = [
  {
    key: "customer",
    label: "Customer App",
    icon: ShoppingBag,
    color: "bg-brand-sky/10 text-brand-sky border-brand-sky/30",
    accentBg: "bg-brand-sky",
    accentHover: "hover:bg-blue-700",
    pwaUrl: "/",
    pkgHint: "in.yourbrand.app.twa",
    description: "The app your customers install to order milk.",
  },
  {
    key: "rider",
    label: "Rider App",
    icon: Truck,
    color: "bg-neutral-700 text-white border-neutral-600",
    accentBg: "bg-neutral-800",
    accentHover: "hover:bg-neutral-900",
    pwaUrl: "/rider.html",
    pkgHint: "in.yourbrand.rider.twa",
    description: "The app your delivery riders use for routes & deliveries.",
  },
  {
    key: "admin",
    label: "Admin App",
    icon: Shield,
    color: "bg-brand-clay/10 text-brand-clay border-brand-clay/30",
    accentBg: "bg-brand-clay",
    accentHover: "hover:bg-orange-700",
    pwaUrl: "/admin.html",
    pkgHint: "in.yourbrand.admin.twa",
    description: "The app you and your operators use to run the dairy.",
  },
];

export default function AdminPwa() {
  const [data, setData] = useState({ by_persona: { customer: [], rider: [], admin: [] } });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/assetlinks");
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-pwa-page">
      <header>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Android Apps</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight inline-flex items-center gap-2">
          <Smartphone className="w-7 h-7"/> TWA · Digital Asset Links
        </h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
          You ship <b>three distinct Android apps</b> from PWA Builder — one per persona. Each has its own
          package name and SHA-256 signing cert. Upload the <code className="px-1 bg-neutral-100 rounded text-xs">assetlinks.json</code>
          for each, and Chrome will hide the URL bar inside every installed app.
        </p>
      </header>

      {/* Live URL */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex items-center gap-3">
        <FileJson className="w-5 h-5 text-brand-leaf shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Live verification URL · serves all 3 personas</div>
          <code className="text-sm font-mono truncate block">{origin}/.well-known/assetlinks.json</code>
        </div>
        <a href="/.well-known/assetlinks.json" target="_blank" rel="noreferrer"
           className="h-9 px-3 rounded-full bg-brand-cream border border-neutral-200 text-xs font-bold inline-flex items-center gap-1.5"
           data-testid="view-assetlinks-link">
          <ExternalLink className="w-3.5 h-3.5"/> View
        </a>
      </div>

      {/* PWA Builder URLs cheat-sheet */}
      <section className="rounded-3xl bg-brand-sand/10 border border-brand-sand/30 p-5 space-y-3">
        <h2 className="font-heading text-base font-extrabold inline-flex items-center gap-2 text-brand-clay">
          <Info className="w-4 h-4"/> Build URLs for PWA Builder
        </h2>
        <p className="text-sm text-neutral-700">Give these URLs to PWA Builder to generate each Android wrapper:</p>
        <div className="space-y-1.5">
          {PERSONAS.map((p) => (
            <CopyableRow key={p.key} label={p.label} value={`${origin}${p.pwaUrl}`}/>
          ))}
        </div>
      </section>

      {/* Three persona sections */}
      {loading ? (
        <LoadingState />
      ) : (
        PERSONAS.map((p) => (
          <PersonaSection
            key={p.key}
            persona={p}
            entries={data.by_persona[p.key] || []}
            reload={load}
          />
        ))
      )}
    </div>
  );
}

function CopyableRow({ label, value }) {
  return (
    <div className="flex items-center gap-2 group">
      <span className="text-xs font-bold uppercase tracking-wider text-neutral-600 w-24 shrink-0">{label}</span>
      <code className="font-mono text-[11px] flex-1 truncate p-2 rounded-lg bg-white border border-neutral-200">{value}</code>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-neutral-100"
              data-testid={`copy-build-${label.toLowerCase().replace(/ /g, "-")}`}
              title="Copy">
        <Copy className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}

function PersonaSection({ persona, entries, reload }) {
  const Icon = persona.icon;
  const [rawJson, setRawJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [addFp, setAddFp] = useState({});

  const upload = async () => {
    if (!rawJson.trim()) { toast.error("Paste the assetlinks JSON first"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/api/admin/assetlinks", {
        persona: persona.key, raw_json: rawJson,
      });
      toast.success(`${persona.label} updated · ${data.saved.length} entry`);
      setRawJson("");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setRawJson(await f.text());
  };

  const addFingerprint = async (pkg) => {
    const fp = (addFp[pkg] || "").trim();
    if (!fp) { toast.error("Enter a SHA-256 fingerprint"); return; }
    setBusy(true);
    try {
      await api.post("/api/admin/assetlinks/fingerprint", {
        persona: persona.key, package_name: pkg, fingerprint: fp,
      });
      toast.success("Fingerprint added");
      setAddFp({ ...addFp, [pkg]: "" });
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const removeFp = async (pkg, fp) => {
    if (!window.confirm(`Remove fingerprint ${fp.slice(0, 20)}…?`)) return;
    setBusy(true);
    try {
      await api.post("/api/admin/assetlinks/fingerprint/remove", {
        persona: persona.key, package_name: pkg, fingerprint: fp,
      });
      toast.success("Removed");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  const removeAll = async () => {
    if (!window.confirm(`Remove ALL entries for ${persona.label}? The installed app will start showing the URL bar.`)) return;
    setBusy(true);
    try {
      await api.post("/api/admin/assetlinks/delete", { persona: persona.key });
      toast.success(`${persona.label} cleared`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className={`rounded-3xl bg-white border-2 border-neutral-100 overflow-hidden`}
             data-testid={`persona-${persona.key}`}>
      {/* Header */}
      <div className={`px-5 py-4 flex items-center gap-3 border-b border-neutral-100 ${persona.color}`}>
        <Icon className="w-5 h-5"/>
        <div className="flex-1">
          <div className="font-heading font-extrabold text-base">{persona.label}</div>
          <div className="text-xs opacity-80">{persona.description}</div>
        </div>
        <span className="text-xs font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white">
          {entries.length} entr{entries.length === 1 ? "y" : "ies"}
        </span>
      </div>

      <div className="p-5 space-y-4">
        {/* Existing entries */}
        {entries.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-neutral-300 bg-brand-cream/40">
            <EmptyState
              icon={Icon}
              size="sm"
              title={`No ${persona.label.toLowerCase()} registered yet`}
              description={<>Upload the <code className="text-[10px] px-1 bg-neutral-100 rounded">assetlinks.json</code> from PWA Builder below.</>}
            />
          </div>
        ) : entries.map((e, idx) => {
          const tgt = e.target || {};
          const pkg = tgt.package_name;
          const fps = tgt.sha256_cert_fingerprints || [];
          return (
            <div key={pkg + idx} className="rounded-2xl bg-brand-cream/40 border border-neutral-200 p-4 space-y-2.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Package</div>
                  <code className="font-mono font-bold text-sm truncate block">{pkg}</code>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold uppercase tracking-wider">
                  <CheckCircle2 className="w-3 h-3"/> {fps.length} SHA
                </span>
              </div>

              <div className="space-y-1.5">
                {fps.map((fp) => (
                  <div key={fp} className="flex items-center gap-2 group">
                    <code className="font-mono text-[10px] flex-1 truncate p-2 rounded-lg bg-white border border-neutral-200" title={fp}>
                      {fp}
                    </code>
                    <button onClick={() => { navigator.clipboard.writeText(fp); toast.success("Copied"); }}
                            className="opacity-0 group-hover:opacity-100 transition w-7 h-7 inline-flex items-center justify-center rounded-lg hover:bg-neutral-100"
                            title="Copy">
                      <Copy className="w-3 h-3"/>
                    </button>
                    <button onClick={() => removeFp(pkg, fp)} disabled={busy}
                            className="opacity-0 group-hover:opacity-100 transition w-7 h-7 inline-flex items-center justify-center rounded-lg text-brand-clay hover:bg-brand-clay/10 disabled:opacity-30"
                            data-testid={`remove-${persona.key}-fp-${fp.slice(0, 5)}`}
                            title="Remove">
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </div>
                ))}
              </div>

              {/* Inline add fingerprint */}
              <div className="flex gap-2 pt-1">
                <Input placeholder="AA:6E:5A:... (Play Store production key)"
                       value={addFp[pkg] || ""}
                       onChange={(ev) => setAddFp({ ...addFp, [pkg]: ev.target.value })}
                       className="h-10 rounded-2xl font-mono text-[11px] flex-1"
                       data-testid={`add-${persona.key}-fp-input-${pkg}`}/>
                <Button onClick={() => addFingerprint(pkg)} disabled={busy}
                        className="h-10 rounded-2xl bg-brand-leaf hover:bg-emerald-700 font-bold text-xs"
                        data-testid={`add-${persona.key}-fp-btn-${pkg}`}>
                  <Plus className="w-3.5 h-3.5 mr-1"/> Add SHA
                </Button>
              </div>
            </div>
          );
        })}

        {/* Upload form */}
        <details className="rounded-2xl border border-neutral-200 bg-brand-cream/30">
          <summary className="px-4 py-3 font-bold text-sm cursor-pointer inline-flex items-center gap-2 list-none">
            <Upload className="w-4 h-4"/> {entries.length ? "Replace" : "Upload"} {persona.label} assetlinks.json
          </summary>
          <div className="p-4 pt-0 space-y-2">
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs">
              <input type="file" accept=".json,application/json"
                     onChange={onFile}
                     className="hidden" data-testid={`${persona.key}-file-input`}/>
              <span className="h-9 px-3 rounded-full bg-white border border-neutral-200 font-bold inline-flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5"/> Choose file…
              </span>
            </label>
            <textarea
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              placeholder={`[{"relation":["delegate_permission/common.handle_all_urls"],"target":{"namespace":"android_app","package_name":"${persona.pkgHint}","sha256_cert_fingerprints":["AA:6E:..."]}}]`}
              className="w-full min-h-[120px] rounded-2xl border border-neutral-200 p-3 font-mono text-[11px] bg-white"
              data-testid={`${persona.key}-json-textarea`}
            />
            <div className="flex gap-2">
              <Button onClick={upload} disabled={busy}
                      className={`h-10 rounded-2xl ${persona.accentBg} ${persona.accentHover} text-white font-bold flex-1`}
                      data-testid={`${persona.key}-upload-btn`}>
                {busy ? "Saving…" : entries.length ? "Replace & Publish" : "Upload & Publish"}
              </Button>
              {entries.length > 0 && (
                <Button onClick={removeAll} disabled={busy} variant="outline"
                        className="h-10 rounded-2xl border-2 border-brand-clay/40 text-brand-clay hover:bg-brand-clay/10 font-bold"
                        data-testid={`${persona.key}-remove-all-btn`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1"/> Remove
                </Button>
              )}
            </div>
          </div>
        </details>
      </div>
    </section>
  );
}
